// Words for stock control (M4): approvals, counts, write-offs and the daily expiry list, in
// plain SA English. One place, so the inbox, the lists and the details say the same thing.

import type {
  Approval,
  CountSummary,
  ReversalOutcome,
  StateTask,
  StockControlSettings,
  WriteOff,
} from '@enp/domain/api';
import { formatCount, formatNumber } from '@enp/domain/format';
import type {
  ApprovalMode,
  ApprovalStatus,
  ApprovalType,
  WriteOffReason,
} from '@enp/domain/stock-control';
import {
  countValueText,
  WRITE_OFF_REASON_LABELS,
  writeOffValueText,
} from '@enp/domain/stock-control';
import { formatDateTime, formatMoney, formatQuantity, type Status } from '@enp/ui';

// ---------------------------------------------------------------- approvals

export const APPROVAL_TYPE_LABEL: Record<ApprovalType, string> = {
  adjustment: 'Count adjustment',
  write_off: 'Write-off',
  correction: 'Correction',
  purchase_request: 'Purchase request',
};

export const APPROVAL_STATUS: Record<ApprovalStatus, { label: string; status: Status }> = {
  requested: { label: 'Waiting for a decision', status: 'warning' },
  approved: { label: 'Approved', status: 'success' },
  rejected: { label: 'Rejected', status: 'danger' },
  cancelled: { label: 'Closed, no decision', status: 'neutral' },
};

/**
 * "Rejected, reversed" only when every row was (UI4A-202): what the rejection actually did,
 * from the server's outcome.
 */
export const REJECTED_LABEL: Record<ReversalOutcome, string> = {
  reversed: 'Rejected, reversed',
  partly_reversed: 'Rejected, partly reversed',
  not_reversed: 'Rejected, not reversed',
};

/** An approval's status in words, a rejection by what it reversed (UI4A-202). */
export function approvalStatus(
  status: ApprovalStatus,
  outcome: ReversalOutcome | null | undefined,
): { label: string; status: Status } {
  if (status === 'rejected' && outcome) {
    return { label: REJECTED_LABEL[outcome], status: 'danger' };
  }
  return APPROVAL_STATUS[status];
}

export const APPROVAL_MODE_LABEL: Record<ApprovalMode, string> = {
  async: 'Sent to the office',
  manager_pin: 'Manager PIN at the station',
};

/** The inbox's views: waiting first, then the history by outcome. */
export const INBOX_VIEWS = ['requested', 'approved', 'rejected', 'cancelled'] as const;
export type InboxView = (typeof INBOX_VIEWS)[number];

export const INBOX_VIEW_LABEL: Record<InboxView, string> = {
  requested: 'Waiting',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Closed',
};

/** A decimal string with its sign in words' place: "+3", "−6", "0". */
export function signedText(value: string, unit: string): string {
  const v = value.trim();
  const negative = v.startsWith('-');
  const abs = negative ? v.slice(1) : v.replace(/^\+/, '');
  const zero = /^0*(\.0*)?$/.test(abs);
  return `${zero ? '' : negative ? '−' : '+'}${formatQuantity(abs, unit, 6)}`;
}

/**
 * The approval's percentage (|change| ÷ level before) as a signed change of the level:
 * "−33.3%". Null when the level before wasn't above zero.
 */
export function percentText(percent: string | null, change: string): string | null {
  if (percent === null) return null;
  const n = formatNumber(percent, { maxDecimals: 1 });
  if (/^0*(\.0*)?$/.test(percent)) return '0%';
  return `${change.trim().startsWith('-') ? '−' : '+'}${n}%`;
}

/**
 * The line's change of the stock level, signed: a count's net change as it is, a write-off
 * as the stock it took away.
 */
export function levelChange(type: ApprovalType, qty: string): string {
  return type === 'write_off' ? `-${qty.trim().replace(/^[-+]/, '')}` : qty;
}

/** What a line's value is worked out from. */
type ValuedLine = { qty: string; value_cents: number | null };

/**
 * A line's value signed the way it moved stock (UI8B-2): a count's loss is −, its gain +; a
 * write-off is −. `null` without a cost. The API's line value is unsigned (|qty| × cost).
 */
export function lineValueCents(type: ApprovalType, line: ValuedLine): number | null {
  if (line.value_cents === null) return null;
  const value = Math.abs(line.value_cents);
  return levelChange(type, line.qty).trim().startsWith('-') ? -value : value;
}

/**
 * What a count or write-off request is worth, as the inbox and its cards say it (UI8B-2,
 * one rule with the stations: UI8X-3): a count's losses and gains each signed, "−R 300.00
 * · +R 50.00"; a write-off "−R 301.25 written off". `null` when nothing has a cost.
 */
export function approvalValueText(a: {
  type: ApprovalType;
  lines: readonly ValuedLine[];
  value_cents: number | null;
}): string | null {
  if (a.type === 'write_off') {
    return a.value_cents === null ? null : writeOffValueText(a.value_cents);
  }
  return countValueText(a.lines.map((l) => lineValueCents(a.type, l)));
}

/** Why a line needed a decision, in words (thresholds when known). */
export function groundsText(
  line: Pick<Approval['lines'][number], 'grounds'>,
  type: ApprovalType,
  settings?: StockControlSettings,
): string[] {
  const out: string[] = [];
  for (const g of line.grounds) {
    if (g === 'value') {
      const limit = settings
        ? type === 'write_off'
          ? settings.writeoff_min_cents
          : settings.count_adjust_min_cents
        : null;
      out.push(
        limit === null ? 'Worth more than the limit' : `Worth ${formatMoney(limit)} or more`,
      );
    } else if (g === 'percent') {
      out.push(
        settings
          ? `Changes the level by ${formatNumber(String(settings.count_adjust_pct), { maxDecimals: 2 })}% or more`
          : 'Changes the level by more than the limit',
      );
    } else if (g === 'cannot_judge') {
      out.push('No cost and no stock before it: always needs a manager');
    } else {
      out.push(g.replace(/_/g, ' '));
    }
  }
  return out;
}

/** The cost the value was judged by: "R 20.00 each" or the no-cost fallback. */
export function costBasisText(
  line: Pick<Approval['lines'][number], 'unit_cost_cents' | 'no_cost' | 'product'>,
): string {
  if (line.no_cost || line.unit_cost_cents === null) return 'No cost: judged by % only';
  const unit = line.product.base_uom.trim();
  // An unknown unit is never guessed as "each" (UI4A-215).
  if (unit === '') return `${formatMoney(line.unit_cost_cents)} (standard cost)`;
  const per = unit === 'ea' ? 'each' : `per ${formatQuantity('1', unit).replace(/^1\s*/, '')}`;
  return `${formatMoney(line.unit_cost_cents)} ${per} (standard cost)`;
}

/**
 * The later counts of `rows`, named with their time, once each: "SC-K7Q-00013 (28 Sep,
 * 07:30)", "SC-K7Q-00013 (…) and SC-K7Q-00014 (…)".
 */
export function countsNamed(
  rows: readonly { count: Approval['later_counts'][number]['count'] }[],
  timeZone: string | undefined,
): string {
  const counts = new Map(rows.map((r) => [r.count.id, r.count]));
  const named = [...counts.values()].map(
    (c) => `${c.number} (${formatDateTime(c.counted_at, { timeZone })})`,
  );
  if (named.length <= 1) return named[0] ?? '';
  return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
}

/** Nothing would be (was) reversed: later counts cover every row (D-055). */
export function nothingReversed(approval: Pick<Approval, 'reversal' | 'later_counts'>): boolean {
  return approval.later_counts.length > 0 && approval.reversal?.outcome === 'not_reversed';
}

/**
 * Says plainly that the fact is already applied (physical facts are never refused, plan
 * §9.3) and what each decision does to it — one sentence for the counted-since case too, so
 * two notices never disagree (UI4A-203).
 */
export function appliedSentence(
  approval: Pick<Approval, 'type' | 'location' | 'later_counts' | 'reversal'>,
  timeZone: string | undefined,
): string {
  const noun = approval.type === 'write_off' ? 'write-off' : 'count';
  const where = approval.location ? ` at ${approval.location.name}` : '';
  const applied = `This ${noun} is already in the stock ledger${where}: the stock figures show it now. Approving confirms it.`;
  if (approval.later_counts.length === 0) {
    return `${applied} Rejecting reverses it with correcting movements.`;
  }
  const counts = countsNamed(approval.later_counts, timeZone);
  const found = approval.later_counts.length === 1 ? 'that count' : 'those counts';
  if (nothingReversed(approval)) {
    return `${applied} Rejecting reverses nothing: every row was counted again in ${counts}, and keeps what ${found} found.`;
  }
  return `${applied} Rejecting reverses the rows nobody has counted since; the rows counted again in ${counts} keep what ${found} found.`;
}

/** "L-K7Q-00488" or the product, when not tracked by lot. */
export function rowName(
  row: { lot: { lot_code: string } | null },
  product: { name: string },
): string {
  return row.lot ? row.lot.lot_code : product.name;
}

/** Why a row isn't reversed (D-055): "counted again in SC-K7Q-00013 (28 Sep, 07:30)". */
export function countedSinceText(
  count: Approval['later_counts'][number]['count'],
  timeZone: string | undefined,
): string {
  return `counted again in ${count.number} (${formatDateTime(count.counted_at, { timeZone })})`;
}

/**
 * Why a row isn't reversed (D-055): "L-K7Q-00481 was counted again in SC-K7Q-00014
 * (25 Sep, 07:30): not reversed, the count stands".
 */
export function laterCountText(
  row: Approval['later_counts'][number],
  timeZone: string | undefined,
): string {
  const what = row.lot ? row.lot.lot_code : row.product.name;
  return `${what} was ${countedSinceText(row.count, timeZone)}: not reversed, the count stands`;
}

/**
 * What the manager confirms before a rejection that leaves counted rows as they are, naming
 * the count and its time (UI4A-207; D-055, the held-action apply's pattern, D-053).
 */
export function rejectAcknowledgement(
  approval: Pick<Approval, 'later_counts' | 'reversal'>,
  timeZone: string | undefined,
): string | null {
  if (approval.later_counts.length === 0) return null;
  const counts = countsNamed(approval.later_counts, timeZone);
  return nothingReversed(approval)
    ? `I understand: what ${counts} found stays, and nothing is reversed`
    : `I understand: what ${counts} found stays, and only the rest is reversed`;
}

/** "Seed loaf", "Seed loaf and 2 more": the approval in the inbox list. */
export function approvalProducts(approval: Pick<Approval, 'lines'>): string {
  const [first, ...rest] = approval.lines;
  if (!first) return 'No products';
  return rest.length === 0 ? first.product.name : `${first.product.name} and ${rest.length} more`;
}

/** The permission deciding needs (plan §10.3), or null when the Admin can't decide it. */
export function decidePermission(
  type: ApprovalType,
): 'writeoff.approve' | 'stock.adjust' | 'purchasing.approve' | null {
  if (type === 'write_off') return 'writeoff.approve';
  if (type === 'adjustment' || type === 'correction') return 'stock.adjust';
  if (type === 'purchase_request') return 'purchasing.approve';
  return null;
}

// ---------------------------------------------------------------- counts

export const COUNT_SCOPE_LABEL: Record<CountSummary['scope'], string> = {
  location: 'Everything at the location',
  products: 'Chosen products',
  product: 'One product',
  lot: 'One lot',
  full: 'Full stock take',
};

export const LINE_KIND: Record<
  'counted' | 'not_counted' | 'new_lot',
  { label: string; explain: string }
> = {
  counted: { label: 'Counted', explain: 'Counted on the shelf.' },
  not_counted: {
    label: 'Not found',
    explain: 'Had stock at the count time but wasn’t counted, so it was counted as 0.',
  },
  new_lot: { label: 'New lot', explain: 'Found without a label: a new lot was made for it.' },
};

/** A count's status in words, with its approval when it has one. */
export function countStatus(c: Pick<CountSummary, 'status' | 'needs_approval' | 'approval'>): {
  label: string;
  status: Status;
} {
  if (c.status === 'superseded') return { label: 'Superseded', status: 'neutral' };
  if (c.approval?.status === 'requested')
    return { label: 'Waiting for approval', status: 'warning' };
  if (c.approval?.status === 'rejected') {
    return approvalStatus('rejected', c.approval.reversal ?? null);
  }
  if (c.approval?.status === 'approved') {
    return {
      label: c.approval.mode === 'manager_pin' ? 'Approved with manager PIN' : 'Approved',
      status: 'success',
    };
  }
  return { label: 'Applied', status: 'success' };
}

// ---------------------------------------------------------------- write-offs

export const REASON_LABEL: Readonly<Record<WriteOffReason, string>> = WRITE_OFF_REASON_LABELS;

/** A write-off's status in words, with its approval when it has one. */
export function writeOffStatus(w: Pick<WriteOff, 'status' | 'approval'>): {
  label: string;
  status: Status;
} {
  if (w.status === 'reversed') return { label: 'Reversed', status: 'danger' };
  // Rejected, but a later count covered it: it stands as counted (D-055, UI4A-202).
  if (w.approval?.status === 'rejected')
    return { label: 'Rejected, not reversed', status: 'danger' };
  if (w.approval?.status === 'requested')
    return { label: 'Waiting for approval', status: 'warning' };
  if (w.approval?.status === 'approved') {
    return {
      label: w.approval.mode === 'manager_pin' ? 'Approved with manager PIN' : 'Approved',
      status: 'success',
    };
  }
  return { label: 'Written off', status: 'neutral' };
}

// ---------------------------------------------------------------- the daily expiry list

export const TASK_STATUS: Record<StateTask['status'], { label: string; status: Status }> = {
  pending: { label: 'To do', status: 'warning' },
  done: { label: 'Done', status: 'success' },
  skipped: { label: 'Skipped', status: 'neutral' },
  // Not done that day: it moves to the next day's list (UI4A-206). A task that came from an
  // earlier day says "Carried over from …" instead.
  rolled_over: { label: 'Carried to next day', status: 'neutral' },
};

/**
 * A quantity with its unit, never a bare number (UI4A-6, UI4A-9): "5 each", "2.5 kg",
 * "3 cups".
 */
export function qtyWithUnit(value: string, unit: string, maxDecimals?: number): string {
  // An unknown unit stays unnamed, never "each" (UI4A-215).
  const text = formatQuantity(value, unit, maxDecimals);
  return unit.trim().toLowerCase() === 'ea' ? `${text} each` : text;
}

/** "Checked 412 of 412 lots with stock": the list's coverage, and whether it is whole. */
export function coverageText(
  evaluated: number,
  withStock: number,
): {
  text: string;
  complete: boolean;
} {
  const lots = withStock === 1 ? 'expiry-managed lot' : 'expiry-managed lots';
  return {
    text: `Checked ${formatCount(evaluated)} of ${formatCount(withStock)} ${lots} with stock`,
    complete: evaluated >= withStock,
  };
}

// ---------------------------------------------------------------- settings

/**
 * Rand typed by a person → cents: "250", "250.5", "1 250.00", "R250". Null when it isn't
 * an amount with at most two decimals.
 */
export function parseRand(text: string): number | null {
  // `\s` covers the non-breaking space the money formatter writes.
  const t = text.replace(/\s/g, '').replace(/^R/i, '');
  const m = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return null;
  const whole = Number(m[1]);
  const fraction = Number((m[2] ?? '').padEnd(2, '0'));
  return whole * 100 + fraction;
}

/** Cents as a person types them back: 25000 → "250.00". */
export function randInput(cents: number): string {
  return `${Math.trunc(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

/** A percentage typed by a person: above 0 and at most 100, up to two decimals. */
export function parsePercent(text: string): number | null {
  const t = text.trim().replace(/%$/, '').trim();
  if (!/^\d{1,3}(?:\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return n > 0 && n <= 100 ? n : null;
}

/** "10 minutes", "1 minute", "Never". */
export function signOutText(minutes: number): string {
  if (minutes === 0) return 'Never';
  if (minutes % 60 === 0 && minutes >= 60) {
    const h = minutes / 60;
    return `${h} ${h === 1 ? 'hour' : 'hours'}`;
  }
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}
